import {useEffect, useState} from 'react';
import {Outlet, Link} from 'react-router';
import '@/styles/global.css';
import styles from '@/app/(auth)/style.module.css';
import {DEFAULT_BRANDING} from '@/util/constants';
import {JotaiProvider} from '@/store/provider';
import {AppInitializer} from '@/components/app-initializer';
import {AuthBrandingOverride} from '@/components/auth-branding-override';
import {AuthBrandingProvider} from '@/contexts/auth-branding';
import {UserSection} from '@/components/layout/user-section';
import {ToastContainer} from '@/components/ui/toast';
import {ClientWrapper} from '@/app/(auth)/client-wrapper';
import {fetchWithTimeout} from '@/util/request-utils';
import {FP_HOST} from '@/util/constants/api';
import type {Branding} from '@/types/branding';

async function fetchBranding(): Promise<Branding[]> {
    const response = await fetchWithTimeout(
        `${FP_HOST}/internal/brandings.json`
    );
    if (!response.ok) return [];
    return response.json();
}

export default function AuthLayout() {
    const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);

    useEffect(() => {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        const brand = parts.length > 2 ? parts[0] : null;

        if (brand && brand !== 'modelbank' && brand !== 'localhost') {
            fetchBranding().then((brands) => {
                const found = brands.find(
                    (b): b is Branding => b?.name === brand
                );
                if (found) setBranding(found);
            });
        }
    }, []);

    const {logo_image, login_image, background_image, background_color} =
        branding;

    const containerStyle: React.CSSProperties = {
        backgroundColor: background_color,
    };
    if (background_image) {
        containerStyle.backgroundImage = `url(${background_image.url})`;
    }

    return (
        <JotaiProvider>
            <AppInitializer />
            <AuthBrandingOverride />
            <AuthBrandingProvider branding={branding}>
                <header
                    className={styles.header}
                    style={{
                        ['--header-text-color' as string]: '255, 255, 255',
                    }}
                >
                    <div className={styles.inner}>
                        <div className={styles.logoContainer}>
                            <Link to="/" className={styles.logo}>
                                <img
                                    src={logo_image?.url ?? ''}
                                    alt="Brand logo"
                                />
                            </Link>
                            <div className={styles.logoText}>Modelbank</div>
                        </div>
                        <UserSection variant="auth" />
                    </div>
                </header>
                <main className={styles.container}>
                    <div
                        className={styles.image}
                        style={{
                            backgroundImage: `url(${login_image?.url})`,
                        }}
                    />
                    <div
                        className={styles.sidebar}
                        data-auth-sidebar
                        style={containerStyle}
                    >
                        <ClientWrapper>
                            <Outlet />
                        </ClientWrapper>
                        <div className={styles.footer}>
                            <a
                                className={styles.poweredBy}
                                href="https://floorplanner.com"
                            >
                                <span>Powered by</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 86 18"
                                    className={styles.iconContainer}
                                >
                                    <path
                                        fill="#828282"
                                        d="M35.366.088c-4.942 0-8.947 3.856-8.947 8.614 0 4.757 4.005 8.613 8.947 8.613 4.942 0 8.948-3.856 8.948-8.613 0-4.758-4.006-8.614-8.948-8.614Zm0 1.284c4.199 0 7.614 3.288 7.614 7.33 0 4.04-3.415 7.33-7.614 7.33-4.198 0-7.614-3.29-7.614-7.33 0-4.042 3.416-7.33 7.614-7.33Z"
                                    />
                                    <path
                                        fill="#828282"
                                        d="m33.689 4.893.674 1.102 1.442 2.37 3.591-1.606-2.117-3.466-3.59 1.6ZM32.644 5.39l-1.63 1.5v4.524l.957.503V8.702l1.4.74v3.21l1.385.724V8.858l-1.453-2.39-.005-.005-.653-1.071V5.39ZM36.574 11.805V9.562l2.2-1.022v2.244l-.643.297-1.557.724Zm-.769-2.656v4.206l3.591-1.605V7.544l-3.59 1.605ZM83.482 5.958h-1.247v6.83h1.378V9.625c0-.05 0-.1.006-.149.018-1.398.592-2.082 1.733-2.057V5.76c-.848.044-1.471.523-1.87 1.43v-1.23ZM79.299 7.363c.256.31.387.715.393 1.206H76.93c.044-.528.194-.938.45-1.231.255-.292.567-.442.947-.442s.704.156.972.467Zm-.904-1.547c-.891 0-1.602.336-2.12 1.013-.517.677-.779 1.535-.779 2.567 0 1.087.256 1.945.773 2.579.517.64 1.197.956 2.045.956.704 0 1.297-.199 1.765-.603.467-.404.779-.913.941-1.528l-1.247-.187c-.236.74-.698 1.106-1.377 1.106-.356 0-.648-.106-.88-.31a1.526 1.526 0 0 1-.467-.728c-.081-.28-.119-.652-.119-1.118h4.196l.006-.174c0-1.05-.237-1.908-.717-2.573-.474-.665-1.147-1-2.02-1ZM69.269 5.96v6.829h1.377V9.278c0-.709.138-1.237.406-1.585.268-.354.585-.528.947-.528.262 0 .487.112.674.342.18.23.274.565.274 1.013v4.27h1.384V8.382c0-.95-.193-1.615-.573-1.994a1.885 1.885 0 0 0-1.378-.572c-.792 0-1.41.398-1.852 1.2V5.96H69.27ZM64.088 9.278c0-.709.138-1.237.406-1.585.268-.354.585-.528.948-.528.261 0 .486.112.673.342.18.23.274.565.274 1.013v4.27h1.384V8.382c0-.95-.193-1.615-.573-1.994a1.885 1.885 0 0 0-1.378-.572c-.792 0-1.41.398-1.852 1.2V5.96h-1.259v6.83h1.378V9.278ZM57.512 11.47a1.07 1.07 0 0 1-.275-.733c0-.95.892-1.423 2.675-1.423v.628c0 .472-.131.895-.4 1.268-.267.372-.666.56-1.19.56-.367 0-.635-.1-.81-.299Zm3.628-4.387a1.707 1.707 0 0 0-.43-.683 1.685 1.685 0 0 0-.754-.448c-.3-.093-.686-.137-1.154-.137-.873 0-1.54.187-2 .553a2.006 2.006 0 0 0-.786 1.405l1.172.193c.112-.709.586-1.063 1.428-1.063.867 0 1.297.355 1.297 1.063v.516c-.17-.007-.3-.007-.393-.007-2.463 0-3.697.783-3.697 2.35 0 .602.187 1.105.554 1.51.368.397.86.596 1.465.596 1.017 0 1.715-.323 2.101-.976v.833h1.316V8.326c0-.59-.038-1-.119-1.243ZM53.173 12.788h1.378V3.56h-1.378v9.228ZM50.455 9.426c0 .665-.1 1.212-.305 1.647-.206.429-.524.64-.966.64-.325 0-.618-.161-.88-.484-.268-.33-.399-.746-.399-1.237v-.976c0-.578.131-1.044.387-1.399.256-.354.567-.534.935-.534.406 0 .71.218.917.653.205.434.312 1 .312 1.69Zm1.465-.099c0-1.025-.2-1.864-.605-2.522-.405-.659-.941-.989-1.621-.989-.823 0-1.446.36-1.87 1.088v-.945h-1.322v9.135h1.403v-3.12c.355.641.928.958 1.714.958.76 0 1.334-.36 1.727-1.075.38-.715.573-1.56.573-2.53ZM23.93 5.76c-.848.043-1.471.522-1.87 1.429v-1.23h-1.247v6.829h1.378V9.625c0-.05 0-.1.006-.149.018-1.398.592-2.082 1.733-2.057V5.76ZM16.73 7.084c.461 0 .804.205 1.022.609.224.398.337.944.337 1.647 0 1.547-.468 2.318-1.396 2.318a1.11 1.11 0 0 1-.25-.025c-.305-.062-.555-.248-.748-.559-.243-.392-.368-.963-.368-1.709 0-.69.113-1.237.343-1.653.181-.33.442-.528.773-.597.093-.018.187-.03.287-.03v-.001Zm-.287-1.255c-.73.069-1.328.404-1.79.994-.53.671-.791 1.51-.791 2.523 0 .938.237 1.771.71 2.498.443.671 1.067 1.032 1.87 1.081.069.006.131.006.2.006.841 0 1.533-.33 2.082-.982.549-.659.823-1.529.823-2.604 0-1.043-.262-1.895-.792-2.548-.53-.652-1.203-.982-2.02-.982-.1 0-.199.007-.292.013ZM10.178 7.083c.462 0 .804.206 1.023.61.224.397.336.944.336 1.646 0 1.548-.467 2.318-1.396 2.318-.424 0-.754-.192-.997-.584-.243-.391-.368-.963-.368-1.708 0-.69.113-1.237.343-1.653.23-.417.586-.628 1.06-.628Zm2.026-.285c-.53-.653-1.202-.983-2.02-.983-.866 0-1.558.336-2.081 1.007-.53.671-.792 1.51-.792 2.523 0 .938.237 1.771.71 2.498.48.727 1.166 1.088 2.07 1.088.841 0 1.533-.33 2.082-.982.549-.659.823-1.53.823-2.604 0-1.044-.262-1.896-.792-2.548ZM4.667 12.788h1.377V3.56H4.667v9.228ZM2.299 5.959c0-.423.012-.702.037-.839a.558.558 0 0 1 .244-.36c.137-.106.374-.156.716-.156.063 0 .181.007.356.025V3.418c-.2-.007-.362-.013-.493-.013-.879 0-1.465.206-1.758.61-.293.403-.436.944-.436 1.609v.335h-.91v1.2h.91v5.63h1.334v-5.63h1.353v-1.2H2.299Z"
                                    />
                                </svg>
                            </a>
                        </div>
                    </div>
                </main>
                <ToastContainer />
            </AuthBrandingProvider>
        </JotaiProvider>
    );
}
